import React from "react";
import {
  Checkbox,
  FormControl,
  InputLabel,
  OutlinedInput,
  Paper,
  Select,
  TextField,
} from "@mui/material";
import styled from "@emotion/styled";
import { useTheme } from "../../../ThemeContext/ThemeContext";

export const StyledOutlinedInput = styled(OutlinedInput)(({ colors }) => ({
  "&.MuiOutlinedInput-root": {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: colors?.border?.default,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors?.primary?.main ,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: colors?.border?.light ,
    },
    "& input": {
      color: colors?.text?.primary ,
      // Fix autofill background color
      "&:-webkit-autofill": {
        WebkitBoxShadow: `0 0 0 1000px ${colors?.background?.paper} inset !important`,
        WebkitTextFillColor: `${colors?.text?.primary} !important`,
        transition: "background-color 5000s ease-in-out 0s",
      },
      "&:-webkit-autofill:hover": {
        WebkitBoxShadow: `0 0 0 1000px ${colors?.background?.paper} inset !important`,
        WebkitTextFillColor: `${colors?.text?.primary} !important`,
      },
      "&:-webkit-autofill:focus": {
        WebkitBoxShadow: `0 0 0 1000px ${colors?.background?.paper} inset !important`,
        WebkitTextFillColor: `${colors?.text?.primary} !important`,
      },
      "&:-webkit-autofill:active": {
        WebkitBoxShadow: `0 0 0 1000px ${colors?.background?.paper} inset !important`,
        WebkitTextFillColor: `${colors?.text?.primary} !important`,
      },
    },
    "& input::placeholder": {
      color: colors?.text?.muted ,
    },
  },
}));

export const StyledTextField = styled(TextField)(
  ({ theme, colors }) => ({
    "& .MuiOutlinedInput-root": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: colors?.border?.default ,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: colors?.primary?.main ,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: colors?.primary?.main ,
      },
      "& input": {
        color: colors?.text?.primary ,
        // Fix autofill background color
        "&:-webkit-autofill": {
          WebkitBoxShadow: `0 0 0 1000px ${colors?.background?.paper} inset !important`,
          WebkitTextFillColor: `${colors?.text?.primary} !important`,
          transition: "background-color 5000s ease-in-out 0s",
        },
        "&:-webkit-autofill:hover": {
          WebkitBoxShadow: `0 0 0 1000px ${colors?.background?.paper} inset !important`,
          WebkitTextFillColor: `${colors?.text?.primary} !important`,
        },
        "&:-webkit-autofill:focus": {
          WebkitBoxShadow: `0 0 0 1000px ${colors?.background?.paper} inset !important`,
          WebkitTextFillColor: `${colors?.text?.primary} !important`,
        },
        "&:-webkit-autofill:active": {
          WebkitBoxShadow: `0 0 0 1000px ${colors?.background?.paper} inset !important`,
          WebkitTextFillColor: `${colors?.text?.primary} !important`,
        },
      },
      "& .MuiInputBase-inputMultiline": {
        color: colors?.text?.primary , 
      },
      "& textarea::placeholder": {
        color: colors?.text?.disabled , 
        opacity: 1,
      },
      "&.Mui-disabled": {
        backgroundColor: colors?.background?.disabled,
      },

      "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
        borderColor: colors?.border?.disabled,
      },

      // Disabled input text (single-line & multiline)
      "&.Mui-disabled .MuiInputBase-input, &.Mui-disabled .MuiInputBase-inputMultiline": {
        WebkitTextFillColor: colors?.text?.disabled,
        color: colors?.text?.disabled,
        opacity: 1,
      },

      // Disabled helper text (catch both sibling and class-based cases)
      "& .MuiFormHelperText-root": {
        color: colors?.text?.muted, // default
      },
      "&.Mui-disabled ~ .MuiFormHelperText-root, & .MuiFormHelperText-root.Mui-disabled": {
        color: colors?.text?.disabled,
      },
    },
    "& .MuiFormHelperText-root": {
      color: colors?.text?.muted ,
    },
    "& .MuiInputLabel-root": {
      color: colors?.text?.disabled ,
      "&.Mui-focused": {
        color: colors?.primary?.main ,
      },
      "&.Mui-error": {
        color: colors?.error?.main ,
      },
      "&.Mui-disabled": {
        color: colors?.text?.muted ,
      },
    },
  })
);

export const StyledDateTimePicker = styled(TextField)(({ colors }) => ({
  "& .MuiOutlinedInput-root": {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: colors?.border?.default ,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors?.primary?.main ,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: colors?.border?.light ,
    },
    "& input": {
      color: colors?.text?.primary ,
      // Fix autofill background color
      "&:-webkit-autofill": {
        WebkitBoxShadow: `0 0 0 1000px ${colors?.background?.paper} inset !important`,
        WebkitTextFillColor: `${colors?.text?.primary} !important`,
        transition: "background-color 5000s ease-in-out 0s",
      },
      "&:-webkit-autofill:hover": {
        WebkitBoxShadow: `0 0 0 1000px ${colors?.background?.paper} inset !important`,
        WebkitTextFillColor: `${colors?.text?.primary} !important`,
      },
      "&:-webkit-autofill:focus": {
        WebkitBoxShadow: `0 0 0 1000px ${colors?.background?.paper} inset !important`,
        WebkitTextFillColor: `${colors?.text?.primary} !important`,
      },
      "&:-webkit-autofill:active": {
        WebkitBoxShadow: `0 0 0 1000px ${colors?.background?.paper} inset !important`,
        WebkitTextFillColor: `${colors?.text?.primary} !important`,
      },
    },
  },
}));

export const StyledInputLabel = styled(InputLabel)(({ colors }) => ({
  color: colors?.text?.secondary ,
  "&.Mui-focused": {
    color: colors?.primary?.main ,
  },
  "&.Mui-error": {
    color: colors?.error?.main ,
  },
}));

export const StyledSelect = styled(Select)(({ colors }) => ({
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: colors?.border?.default,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: colors?.primary?.main,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: colors?.border?.light,
  },

  "& .MuiSelect-select": {
    color: colors?.text?.primary,
  },

  "& .MuiSelect-icon": {
    color: colors?.text?.muted,
  },

  "& .MuiInputLabel-root": {
    color: colors?.text?.muted,
  },

  /* 🔹 Disabled State */
  "&.Mui-disabled": {
    backgroundColor: colors?.background?.disabled,
  },
  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: colors?.border?.disabled,
  },
  "&.Mui-disabled .MuiSelect-select": {
    color: colors?.text?.disabled,
    WebkitTextFillColor: colors?.text?.disabled, // fix Safari/Chrome autofill
  },
  "&.Mui-disabled .MuiSelect-icon": {
    color: colors?.text?.disabled,
  },
  "&.Mui-disabled .MuiInputLabel-root": {
    color: colors?.text?.disabled,
  },
}));


export const StyledFormControl = styled(FormControl)(
  ({ theme, colors }) => ({
    "& .MuiOutlinedInput-root": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: colors?.border?.default ,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: colors?.primary?.main ,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: colors?.border?.light ,
      },
      "& input": {
        color: colors?.text?.primary ,
      },
    },
    "& .MuiInputLabel-root": {
      color: colors?.text?.secondary ,
      "&.Mui-focused": {
        color: colors?.primary?.main ,
      },
      "&.Mui-error": {
        color: colors?.error?.main ,
      },
    },
  })
);

export const StyledPaper = styled(Paper)(({ theme, colors }) => ({
  backgroundColor: colors?.background?.paper ,
  boxShadow: colors?.shadow?.default ,
  borderRadius: "0.5rem",
  border: `1px solid ${colors?.border?.card }`,
}));

export const StyledCheckbox = styled(Checkbox)(({ colors }) => ({
  color: colors?.text?.muted ,
  "&.MuiCheckbox-indeterminate": {
    color: colors?.primary?.main ,
  },
  "&.Mui-checked": {
    color: colors?.primary?.main ,
  },
  "&:hover": {
    backgroundColor: colors?.hover?.muted ,
  },
  "&.Mui-disabled": {
      color: colors?.text?.disabledCheckbox ,
  },
}));

// Theme-aware wrapper components
export const ThemedOutlinedInput = React.forwardRef((props, ref) => {
  const { colors } = useTheme();
  return <StyledOutlinedInput ref={ref} colors={colors} {...props} />;
});

export const ThemedTextField = React.forwardRef((props, ref) => {
  const { colors } = useTheme();
  return <StyledTextField ref={ref} colors={colors} {...props} />;
});

export const ThemedDateTimePicker = React.forwardRef((props, ref) => {
  const { colors } = useTheme();
  return <StyledDateTimePicker ref={ref} colors={colors} {...props} />;
});

export const ThemedInputLabel = React.forwardRef((props, ref) => {
  const { colors } = useTheme();
  return <StyledInputLabel ref={ref} colors={colors} {...props} />;
});

export const ThemedSelect = React.forwardRef((props, ref) => {
  const { colors } = useTheme();
  return <StyledSelect ref={ref} colors={colors} {...props} />;
});

export const ThemedFormControl = React.forwardRef((props, ref) => {
  const { colors } = useTheme();
  return <StyledFormControl ref={ref} colors={colors} {...props} />;
});

export const ThemedPaper = React.forwardRef((props, ref) => {
  const { colors } = useTheme();
  return <StyledPaper ref={ref} colors={colors} {...props} />;
});

export const ThemedCheckbox = React.forwardRef((props, ref) => {
  const { colors } = useTheme();
  return <StyledCheckbox ref={ref} colors={colors} {...props} />;
});
